import React, { useEffect, useState } from "react";

/**
 * SeasonRecapShareModal — shows the user's 1080×1080 season recap
 * with quick share/download/copy actions. Designed to be triggered
 * from the player dashboard or auto-popped at season end.
 *
 * The image itself is served from `/api/season-recap/{user_id}/png`
 * — a real URL that previews correctly in Twitter cards, Discord
 * embeds, and Telegram. We append a `?v=<timestamp>` query in the
 * preview to bust any browser cache, but the server-side cache is
 * keyed by stats hash and survives the bust.
 */
export function SeasonRecapShareModal({ open, userId, onClose, onToast }) {
  const [version, setVersion] = useState(0);

  // Bump the cache-buster every time the modal opens so the player
  // always sees freshly-rendered stats.
  useEffect(() => { if (open) setVersion(Date.now()); }, [open]);

  if (!open || !userId) return null;

  // Build absolute URL so when the user clicks "Copy Link" or pastes
  // it elsewhere, the link still resolves on production (not just
  // the current preview pod). `window.location.origin` always equals
  // the same domain the user is browsing.
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = `${origin}/api/season-recap/${userId}/png`;
  const previewSrc = `${shareUrl}?v=${version}`;
  const profileUrl = `${origin}/players/${userId}`;

  const copyImageLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      onToast?.("Link copied — paste anywhere.");
    } catch (_e) {
      onToast?.("Copy failed.");
    }
  };

  const tweetText = encodeURIComponent(`My LEAGUE HUB season recap. ${profileUrl}`);
  const twitterUrl =
    `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <div
      data-testid="season-recap-modal"
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl border border-[#14b8a6]/40 bg-[#070c0b]">
        <header className="p-4 flex items-center justify-between border-b border-white/5">
          <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
            <i className="fas fa-share-nodes mr-1" />SEASON RECAP
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="season-recap-close"
            className="text-white/50 hover:text-white text-lg"
          >
            ×
          </button>
        </header>

        <div className="p-4 space-y-3">
          <img
            src={previewSrc}
            alt="Season recap"
            data-testid="season-recap-preview-img"
            className="w-full rounded-xl border border-white/5 aspect-square object-cover"
          />

          <div className="grid grid-cols-2 gap-2">
            <a
              href={previewSrc}
              download={`leaguehub-recap-${userId}.png`}
              data-testid="season-recap-download"
              className="text-center text-[11px] tracking-widest font-bold py-2.5 rounded-xl bg-white/5 text-white border border-white/10 hover:border-[#14b8a6]/40"
            >
              <i className="fas fa-download mr-1" />DOWNLOAD
            </a>
            <button
              type="button"
              onClick={copyImageLink}
              data-testid="season-recap-copy"
              className="text-[11px] tracking-widest font-bold py-2.5 rounded-xl bg-white/5 text-white border border-white/10 hover:border-[#14b8a6]/40"
            >
              <i className="fas fa-link mr-1" />COPY LINK
            </button>
            <a
              href={twitterUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="season-recap-tweet"
              className="col-span-2 text-center text-[11px] tracking-widest font-bold py-2.5 rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#0d9488] text-black hover:brightness-110"
            >
              <i className="fa-brands fa-x-twitter mr-1" />POST TO X
            </a>
          </div>

          <p className="text-[10px] text-white/40 text-center mt-2">
            Pasting this link on Twitter / Discord / Instagram auto-embeds the card.
          </p>
        </div>
      </div>
    </div>
  );
}
